import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import {
  CancelOutlined,
  CheckCircleOutlined,
  InfoOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";

import { lighten, useTheme } from "@mui/material/styles";
import { formatDateWithTimestamp } from "../../utils/formatDate";
import {
  OVERALL_DOCUMENT_STATUS_COLORS,
  OVERALL_DOCUMENT_STATUS_LABELS,
  TRANSMISSION_STATUS_MAPPING,
} from "../../constants/peppolStatusConstants";
import StatusChip from "../../components/StatusChip";
import {
  deriveStatusFromMetadata,
  DocumentStatus,
} from "../../utils/derivePeppolStatus";
import {
  B2C_ELECTRONIC_ADDRESS,
  DocumentSource,
} from "../../constants/appConstants";
import { useGetPeppolDataHandler } from "./commonDataHandlers/getPeppolStatusHandler";

const backgroundOpacity = 0.96;
const iconSize = 30;

// Centralized color configuration for cards using theme colors
const getCardColorConfig = (theme) => ({
  info: {
    styles: {
      border: `0.1px solid ${theme.palette.info.main}`,
      backgroundColor: lighten(theme.palette.info.main, backgroundOpacity),
    },
    icon: (
      <InfoOutlined
        sx={{ color: theme.palette.info.main, fontSize: iconSize }}
      />
    ),
  },
  error: {
    styles: {
      border: `0.1px solid ${theme.palette.error.main}`,
      backgroundColor: lighten(theme.palette.error.main, backgroundOpacity),
    },
    icon: (
      <CancelOutlined
        sx={{ color: theme.palette.error.main, fontSize: iconSize }}
      />
    ),
  },
  warning: {
    styles: {
      border: `0.1px solid ${theme.palette.warning.main}`,
      backgroundColor: lighten(theme.palette.warning.main, backgroundOpacity),
    },
    icon: (
      <WarningAmberOutlined
        sx={{ color: theme.palette.warning.main, fontSize: iconSize }}
      />
    ),
  },
  success: {
    styles: {
      border: `0.1px solid ${theme.palette.success.main}`,
      backgroundColor: lighten(theme.palette.success.main, backgroundOpacity),
    },
    icon: (
      <CheckCircleOutlined
        sx={{ color: theme.palette.success.main, fontSize: iconSize }}
      />
    ),
  },
  default: {
    styles: {
      border: `0.1px solid ${theme.palette.grey[500]}`,
      backgroundColor: lighten(theme.palette.grey[500], backgroundOpacity),
    },
    icon: (
      <InfoOutlined
        sx={{ color: theme.palette.grey[600], fontSize: iconSize }}
      />
    ),
  },
});

// Unified Status Card Component
const StatusCard = ({
  title,
  status,
  statusColor,
  receiverId,
  senderId,
  lastUpdated,
  cardStyles,
  as4MessageId,
  hideAs4MessageId = false,
}) => {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 2,
        ...cardStyles,
      }}
    >
      <Box sx={{ p: 2 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
          flexWrap="wrap"
          gap={1}
        >
          <Typography
            variant="body1"
            component="h3"
            fontWeight="600"
            sx={{
              wordBreak: "break-word",
              flex: "1 1 auto",
              minWidth: 0,
            }}
          >
            {title}
          </Typography>
          <StatusChip
            color={statusColor}
            label={status}
            size="small"
            icon="none"
            sx={{
              flexShrink: 0,
            }}
          />
        </Box>

        {/* Details Section */}
        <Box mt={1} mb={1} dir="ltr" sx={{ textAlign: "left" }}>
          {senderId && (
            <Stack direction="row" mb={1} flexWrap="wrap">
              <Typography
                variant="body2"
                component="span"
                fontWeight={500}
                color="text.secondary"
                mr={0.5}
                sx={{
                  wordBreak: "break-word",
                }}
              >
                Sender ID:
              </Typography>
              <Typography
                variant="body2"
                component="span"
                fontWeight={500}
                color="text.secondary"
                mr={0.5}
                sx={{
                  wordBreak: "break-all",
                  minWidth: 0,
                }}
              >
                {senderId}
              </Typography>
            </Stack>
          )}
          {receiverId && (
            <Stack direction="row" mb={1} flexWrap="wrap">
              <Typography
                variant="body2"
                component="span"
                fontWeight={500}
                color="text.secondary"
                mr={0.5}
                sx={{
                  wordBreak: "break-word",
                }}
              >
                Receiver ID:
              </Typography>
              <Typography
                variant="body2"
                component="span"
                fontWeight={500}
                sx={{
                  wordBreak: "break-all",
                  minWidth: 0,
                }}
              >
                {receiverId}
              </Typography>
            </Stack>
          )}
          {!hideAs4MessageId && (
            <Stack direction="row" mb={1} flexWrap="wrap">
              <Typography
                variant="body2"
                component="span"
                fontWeight={500}
                color="text.secondary"
                mr={0.5}
                sx={{
                  wordBreak: "break-word",
                }}
              >
                AS4 Message ID:
              </Typography>
              <Typography
                variant="body2"
                component="span"
                fontWeight={500}
                sx={{
                  wordBreak: "break-all",
                  minWidth: 0,
                }}
              >
                {as4MessageId}
              </Typography>
            </Stack>
          )}
        </Box>
        <Divider sx={{ mb: 1 }} />
        <Typography
          color="text.secondary"
          variant="body2"
          fontWeight={500}
          sx={{
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }}
        >
          Last updated: {lastUpdated}
        </Typography>
      </Box>
    </Card>
  );
};

const StatusCardSkeleton = () => {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: 2,
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
          gap={1}
        >
          <Skeleton variant="text" width="45%" height={30} />
          <Skeleton variant="rounded" width={90} height={24} />
        </Box>

        <Box mt={1} mb={1}>
          {[...Array(3)].map((_, index) => (
            <Stack direction="row" mb={1} key={index}>
              <Skeleton variant="text" width={80} height={22} sx={{ mr: 1 }} />
              <Skeleton variant="text" width="60%" height={22} />
            </Stack>
          ))}
        </Box>

        <Divider sx={{ mb: 1 }} />
        <Skeleton variant="text" width="65%" height={22} />
      </Box>
    </Card>
  );
};

// Common Error Component
const CornerErrorBox = ({ title, message, severity, validationErrors }) => {
  const theme = useTheme();
  const CARD_COLOR_CONFIG = getCardColorConfig(theme);
  const cardStyles =
    CARD_COLOR_CONFIG[severity]?.styles || CARD_COLOR_CONFIG.default.styles;
  const iconColor = theme.palette[severity]?.main;

  return (
    <Card
      sx={{
        borderRadius: 2,
        p: 2,
        ...cardStyles,
      }}
    >
      <Box display="flex" alignItems="center" mb={2}>
        <CancelOutlined
          sx={{
            fontSize: 24,
            mr: 1,
            color: iconColor,
          }}
        />
        <Typography variant="body1" component="h3" fontWeight="600">
          {title}
        </Typography>
      </Box>

      {validationErrors && validationErrors.length > 0 && (
        <Box component="ul" sx={{ pl: 2, m: 0, mb: 0 }}>
          {validationErrors.map((error, index) => {
            const [code, msg] = error.text.split(/]-(.*)/, 2);
            return (
              <Box component="li" key={index} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight="500" color="error.main">
                  {code.toUpperCase() + "]"}
                </Typography>
                <Typography variant="body2" sx={{ pl: 2 }}>
                  {msg}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Card>
  );
};

const DocumentDetailedPeppolStatus = ({
  documentType,
  docId,
  metaInfo,
  isPurchase = false,
  buyer = null,
  documentSource = null,
}) => {
  const { peppolData, isLoading } = useGetPeppolDataHandler(
    documentType,
    docId,
    isPurchase,
    {},
    metaInfo,
  );
  const [otaStatus, setOtaStatus] = useState({});
  const [participantStatus, setParticipantStatus] = useState({});
  const [overallStatus, setOverallStatus] = useState({});
  const [extractedErrors, setExtractedErrors] = useState({});
  const theme = useTheme();
  const CARD_COLOR_CONFIG = getCardColorConfig(theme);
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const isB2CCustomer = buyer?.electronic_address === B2C_ELECTRONIC_ADDRESS;
    const isPeppol = documentSource === DocumentSource.PEPPOL;
    const overallStatusVALUE = deriveStatusFromMetadata(
      peppolData,
      isPurchase,
      isB2CCustomer,
      documentSource,
    );
    const extractedErrors = errorExtractor(
      peppolData,
      isPurchase,
      isB2CCustomer,
    );

    const otaStatus = peppolData?.toC5;
    const participantStatus = isPeppol ? peppolData?.toC2 : peppolData?.toC3;

    const overallStatus = {
      title: "Overall Status",
      lastUpdated: formatDateWithTimestamp(peppolData?.lastUpdated),
      status:
        OVERALL_DOCUMENT_STATUS_LABELS?.[overallStatusVALUE]?.toUpperCase() ??
        "N/A",
      statusColor:
        OVERALL_DOCUMENT_STATUS_COLORS?.[overallStatusVALUE] || "default",
      senderId: !isPeppol && peppolData?.issuedBy,
      receiverId: isPeppol && peppolData?.issuedTo,
      cardStyles:
        CARD_COLOR_CONFIG[
          OVERALL_DOCUMENT_STATUS_COLORS?.[overallStatusVALUE] || "default"
        ].styles,
    };

    const c3orc2Status = {
      title: `${isPeppol ? "Sender" : "Receiver"} Status`,
      lastUpdated: formatDateWithTimestamp(participantStatus?.lastUpdated),
      status:
        participantStatus?.status === DocumentStatus.SKIPPED
          ? "SKIPPED"
          : TRANSMISSION_STATUS_MAPPING[participantStatus?.status]
              ?.displayStatus || "N/A",
      statusColor:
        TRANSMISSION_STATUS_MAPPING[participantStatus?.status]?.color ||
        "default",
      senderId: isPeppol && peppolData?.issuedBy,
      receiverId: !isPeppol && peppolData?.issuedTo,
      as4MessageId: participantStatus?.transmissionResponse?.messageId,
      cardStyles:
        CARD_COLOR_CONFIG[
          TRANSMISSION_STATUS_MAPPING[participantStatus?.status]?.color ||
            "default"
        ].styles,
    };

    const c5Status = {
      title: "OTA Status",
      status:
        TRANSMISSION_STATUS_MAPPING[otaStatus?.status]?.displayStatus || "N/A",
      statusColor:
        TRANSMISSION_STATUS_MAPPING[otaStatus?.status]?.color || "default",
      receiverId: otaStatus?.receiverId ?? "NA",
      as4MessageId: otaStatus?.transmissionResponse?.messageId,
      lastUpdated: formatDateWithTimestamp(otaStatus?.lastUpdated),
      cardStyles:
        CARD_COLOR_CONFIG[
          TRANSMISSION_STATUS_MAPPING[otaStatus?.status]?.color || "default"
        ].styles,
    };

    setOverallStatus(overallStatus);
    setOtaStatus(c5Status);
    setParticipantStatus(c3orc2Status);
    setExtractedErrors(extractedErrors);
  }, [
    buyer?.electronic_address,
    documentSource,
    isLoading,
    isPurchase,
    metaInfo,
    peppolData,
  ]);

  return (
    <Grid container spacing={2} mb={2}>
      <Grid size={{ xs: 12, md: 4 }}>
        {isLoading ? (
          <StatusCardSkeleton />
        ) : (
          <StatusCard {...overallStatus} hideAs4MessageId={true} />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isLoading ? (
          <StatusCardSkeleton />
        ) : (
          <StatusCard {...participantStatus} />
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        {isLoading ? <StatusCardSkeleton /> : <StatusCard {...otaStatus} />}
      </Grid>

      {extractedErrors?.C2Error && (
        <Grid size={{ xs: 12 }}>
          <CornerErrorBox
            title="Document Validation Errors"
            message={`Found ${extractedErrors.C2Error.count} validation errors that need to be fixed before the document can be processed.`}
            severity="error"
            validationErrors={extractedErrors.C2Error.errors}
          />
        </Grid>
      )}

      {extractedErrors?.C3orC2TransmissionError && (
        <Grid size={{ xs: 12 }}>
          <CornerErrorBox
            title={`${isPurchase ? "C2" : "C3"} Transmission Errors`}
            message={`Found ${extractedErrors.C3orC2TransmissionError.count} transmission errors that prevented the document from being sent successfully.`}
            severity="error"
            validationErrors={extractedErrors.C3orC2TransmissionError.errors}
          />
        </Grid>
      )}

      {extractedErrors?.C5TransmissionError && (
        <Grid size={{ xs: 12 }}>
          <CornerErrorBox
            title="C5 Transmission Errors"
            message={`Found ${extractedErrors.C5TransmissionError.count} transmission errors that prevented the document from being sent successfully.`}
            severity="error"
            validationErrors={extractedErrors.C5TransmissionError.errors}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default DocumentDetailedPeppolStatus;

// Error extractor function
const errorExtractor = (peppolData, isPurchase, isB2CCustomer = false) => {
  const validationResults = peppolData?.validationResults;
  const to_c5 = peppolData?.toC5;
  const to_c3_or_c2 = isPurchase ? peppolData?.toC2 : peppolData?.toC3;

  let c2OrC3Errors = null;
  if (validationResults && validationResults.length > 0) {
    c2OrC3Errors = validationResults.map((error) => ({
      location: error.location,
      test: error.test,
      text: error.text,
    }));
  }

  let c3OrC2TransmissionErrors = null;
  if (
    to_c3_or_c2?.transmissionError === true &&
    to_c3_or_c2?.transmissionErrors &&
    to_c3_or_c2.transmissionErrors.length > 0
  ) {
    c3OrC2TransmissionErrors = to_c3_or_c2.transmissionErrors.map((error) => ({
      text: error,
    }));
  }

  let c5TransmissionErrors = null;
  if (
    to_c5?.transmissionError === true &&
    to_c5?.transmissionErrors &&
    to_c5.transmissionErrors.length > 0
  ) {
    c5TransmissionErrors = to_c5.transmissionErrors.map((error) => ({
      text: error,
    }));
  }

  return {
    C2Error:
      c2OrC3Errors && c2OrC3Errors.length > 0
        ? {
            title: `${isPurchase ? "C2" : "C3"} Document Validation Errors`,
            errors: c2OrC3Errors,
            count: c2OrC3Errors.length,
          }
        : null,
    C3orC2TransmissionError:
      c3OrC2TransmissionErrors && c3OrC2TransmissionErrors.length > 0
        ? {
            title: `${isPurchase ? "C2" : "C3"} Transmission Errors`,
            errors: c3OrC2TransmissionErrors,
            count: c3OrC2TransmissionErrors.length,
          }
        : null,
    C5TransmissionError:
      c5TransmissionErrors && c5TransmissionErrors.length > 0
        ? {
            title: "C5 Transmission Errors",
            errors: c5TransmissionErrors,
            count: c5TransmissionErrors.length,
          }
        : null,
  };
};
